import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowRight,
  ExternalLink,
  Filter,
  Grid3X3,
  LayoutList,
  Search,
  Award,
  Briefcase
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import PublicHeader from './components/PublicHeader';
import PublicFooter from './components/PublicFooter';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PortfolioPage = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await axios.get(`${API}/public/portfolio`);
        const data = response.data || [];
        setProjects(data);
        setFilteredProjects(data);
        
        // Extract unique categories
        const cats = [...new Set(data.map(p => p.category).filter(Boolean))];
        setCategories(cats);
      } catch (err) {
        setProjects([]);
        setFilteredProjects([]);
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  useEffect(() => {
    let filtered = [...projects];
    
    if (activeCategory !== 'all') {
      filtered = filtered.filter(p => p.category === activeCategory);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        p.title.toLowerCase().includes(query) ||
        (p.client_name && p.client_name.toLowerCase().includes(query)) ||
        (p.description && p.description.toLowerCase().includes(query))
      );
    }
    
    setFilteredProjects(filtered);
  }, [activeCategory, searchQuery, projects]);

  return (
    <div className="min-h-screen bg-white">
      <PublicHeader />

      {/* Hero Section */}
      <section 
        className="pt-40 pb-16 lg:pt-48 lg:pb-20"
        style={{ background: `linear-gradient(to bottom right, var(--color-hero-bg-from, #F8FAFC), white, var(--color-hero-bg-to, #EFF6FF))` }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6"
              style={{ backgroundColor: 'var(--color-primary, #1F3C4F)1a', color: 'var(--color-primary, #1F3C4F)' }}
            >
              <Award className="w-4 h-4" />
              Our Work
            </div>
            <h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6"
              style={{ color: 'var(--color-heading-text, #0F172A)' }}
            >
              Portfolio
            </h1>
            <p 
              className="text-xl leading-relaxed"
              style={{ color: 'var(--color-body-text, #475569)' }}
            >
              Explore our work across brand strategy, digital marketing, web development, 
              and creative services. Each project represents a partnership built on strategic 
              thinking and creative excellence.
            </p>
          </div>
        </div>
      </section>

      {/* Filter Bar */}
      <section className="py-8 border-b border-slate-100 sticky top-20 bg-white z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="flex flex-wrap gap-2">
              <Button
                variant={activeCategory === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveCategory('all')}
                style={activeCategory === 'all' ? { backgroundColor: 'var(--color-button-bg, var(--color-primary, #1F3C4F))' } : {}}
                data-testid="filter-all"
              >
                All Projects
              </Button>
              {categories.map((cat) => (
                <Button
                  key={cat}
                  variant={activeCategory === cat ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setActiveCategory(cat)}
                  style={activeCategory === cat ? { backgroundColor: 'var(--color-button-bg, var(--color-primary, #1F3C4F))' } : {}}
                  data-testid={`filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {cat}
                </Button>
              ))}
            </div>
            
            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input 
                  placeholder="Search projects..." 
                  className="pl-10 w-64"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="portfolio-search"
                />
              </div>
              <div className="hidden sm:flex items-center gap-1 border rounded-lg p-1">
                <button 
                  className={`p-2 rounded ${viewMode === 'grid' ? 'bg-slate-100' : ''}`}
                  onClick={() => setViewMode('grid')}
                  data-testid="view-grid"
                >
                  <Grid3X3 className="w-4 h-4" />
                </button>
                <button 
                  className={`p-2 rounded ${viewMode === 'list' ? 'bg-slate-100' : ''}`}
                  onClick={() => setViewMode('list')}
                  data-testid="view-list"
                >
                  <LayoutList className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Grid */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-20">
              <div 
                className="animate-spin rounded-full h-12 w-12 border-b-2"
                style={{ borderColor: 'var(--color-primary, #1F3C4F)' }}
              ></div>
            </div>
          ) : projects.length === 0 ? (
            <div className="text-center py-20">
              <div 
                className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center"
                style={{ backgroundColor: 'var(--color-section-alt-bg, #F1F5F9)' }}
              >
                <Briefcase className="w-10 h-10" style={{ color: 'var(--color-muted-text, #94A3B8)' }} />
              </div>
              <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--color-heading-text, #0F172A)' }}>
                Portfolio Coming Soon
              </h3>
              <p className="text-lg max-w-md mx-auto" style={{ color: 'var(--color-muted-text, #94A3B8)' }}>
                We're currently updating our portfolio with new projects. Check back soon to see our latest work.
              </p>
              <Link to="/contact" className="mt-6 inline-block">
                <Button 
                  style={{ 
                    backgroundColor: 'var(--color-button-bg, var(--color-primary, #1F3C4F))',
                    color: 'var(--color-button-text, #FFFFFF)',
                    borderRadius: 'var(--btn-computed-radius, 0.5rem)'
                  }}
                >
                  Get in Touch
                </Button>
              </Link>
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-lg" style={{ color: 'var(--color-muted-text, #94A3B8)' }}>No projects found matching your criteria.</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => { setActiveCategory('all'); setSearchQuery(''); }}
              >
                Clear Filters
              </Button>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProjects.map((project) => (
                <Link 
                  key={project.id} 
                  to={`/portfolio/${project.slug}`}
                  data-testid={`portfolio-card-${project.slug}`}
                >
                  <Card 
                    className="border-0 shadow-lg overflow-hidden group cursor-pointer h-full hover:shadow-xl transition-shadow"
                    style={{ backgroundColor: 'var(--color-card-bg, #FFFFFF)' }}
                  >
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img 
                        src={project.featured_image || 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop'} 
                        alt={project.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      {project.is_featured && (
                        <Badge style={{ backgroundColor: 'var(--color-button-bg, var(--color-primary, #1F3C4F))' }} className="absolute top-4 left-4">Featured</Badge>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <CardContent className="p-6">
                      {project.category && (
                        <Badge variant="outline" className="mb-3">{project.category}</Badge>
                      )}
                      <h3 
                        className="text-xl font-bold mb-2 transition-colors"
                        style={{ color: 'var(--color-heading-text, #0F172A)' }}
                      >
                        {project.title}
                      </h3>
                      {project.client_name && (
                        <p className="text-sm mb-3" style={{ color: 'var(--color-muted-text, #94A3B8)' }}>{project.client_name}</p>
                      )}
                      <p className="text-sm line-clamp-2" style={{ color: 'var(--color-body-text, #475569)' }}>{project.description}</p>
                      {project.technologies && project.technologies.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-4">
                          {project.technologies.slice(0, 3).map((tech, idx) => (
                            <span 
                              key={idx} 
                              className="text-xs px-2 py-1 rounded"
                              style={{ backgroundColor: 'var(--color-section-alt-bg, #F1F5F9)', color: 'var(--color-body-text, #475569)' }}
                            >
                              {tech}
                            </span>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          ) : (
            <div className="space-y-6">
              {filteredProjects.map((project) => (
                <Link 
                  key={project.id} 
                  to={`/portfolio/${project.slug}`}
                  data-testid={`portfolio-list-${project.slug}`}
                >
                  <Card 
                    className="border-0 shadow-lg overflow-hidden group cursor-pointer hover:shadow-xl transition-shadow"
                    style={{ backgroundColor: 'var(--color-card-bg, #FFFFFF)' }}
                  >
                    <div className="grid md:grid-cols-3 gap-0">
                      <div className="relative aspect-video md:aspect-auto overflow-hidden">
                        <img 
                          src={project.featured_image || 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop'} 
                          alt={project.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        {project.is_featured && (
                          <Badge style={{ backgroundColor: 'var(--color-button-bg, var(--color-primary, #1F3C4F))' }} className="absolute top-4 left-4">Featured</Badge>
                        )}
                      </div>
                      <CardContent className="p-6 md:col-span-2 flex flex-col justify-center">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            {project.category && (
                              <Badge variant="outline" className="mb-2">{project.category}</Badge>
                            )}
                            <h3 
                              className="text-2xl font-bold transition-colors"
                              style={{ color: 'var(--color-heading-text, #0F172A)' }}
                            >
                              {project.title}
                            </h3>
                          </div>
                          <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-all" style={{ color: 'var(--color-muted-text, #94A3B8)' }} />
                        </div>
                        {project.client_name && (
                          <p className="text-sm mb-3" style={{ color: 'var(--color-muted-text, #94A3B8)' }}>{project.client_name}</p>
                        )}
                        <p style={{ color: 'var(--color-body-text, #475569)' }}>{project.description}</p>
                        {project.technologies && project.technologies.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-4">
                            {project.technologies.map((tech, idx) => (
                              <span 
                                key={idx} 
                                className="text-xs px-2 py-1 rounded"
                                style={{ backgroundColor: 'var(--color-section-alt-bg, #F1F5F9)', color: 'var(--color-body-text, #475569)' }}
                              >
                                {tech}
                              </span>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="py-20 lg:py-28"
        style={{ backgroundColor: 'var(--color-button-bg, var(--color-primary, #1F3C4F))' }}
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Start Your Project?
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Let's discuss how we can bring the same strategic thinking and creative excellence 
            to your marketing challenges.
          </p>
          <Link to="/contact">
            <Button 
              size="lg" 
              className="text-lg px-8 py-6"
              style={{ 
                backgroundColor: 'var(--color-button-secondary-bg, #FFFFFF)',
                color: 'var(--color-primary, #1F3C4F)',
                borderRadius: 'var(--btn-computed-radius, 0.5rem)'
              }}
            >
              Get in Touch
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default PortfolioPage;
